import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from 'react';
import { GET_CURRENT_COMPANY } from '../api';
import { Company } from '@employ-engage/data';
import { useApolloClient } from '@apollo/client';
import { doesSessionExist } from 'supertokens-web-js/recipe/session';

type CurrentCompanyContextType = {
  currentCompany: Company;
  refetchCompany: () => Promise<any>;
  loading: boolean;
};

const CurrentCompanyContext = createContext<
  CurrentCompanyContextType | undefined
>(undefined);

export const CurrentCompanyProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const client = useApolloClient();
  const [currentCompany, setCurrentCompany] = useState<Company>(
    {} as Company
  );
  const [loading, setLoading] = useState<boolean>(true);

  const fetchCompany = useCallback(async () => {
    const sessionExists = await doesSessionExist();
    if (!sessionExists) {
      setLoading(false);
      return;
    }

    try {
      const { data } = await client.query({
        query: GET_CURRENT_COMPANY,
        fetchPolicy: 'network-only',
      });
      if (data?.currentCompany) {
        setCurrentCompany(data.currentCompany);
      }
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  return (
    <CurrentCompanyContext.Provider
      value={{ currentCompany, refetchCompany: fetchCompany, loading }}
    >
      {children}
    </CurrentCompanyContext.Provider>
  );
};

export const useCurrentCompany = () => {
  const context = useContext(CurrentCompanyContext);
  if (!context) {
    throw new Error(
      'useCurrentCompany must be used within a CurrentCompanyProvider'
    );
  }
  return context;
};
